'use client'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
    motion,
    useMotionValueEvent,
    useReducedMotion,
    useScroll,
} from 'framer-motion'
import { AnimatedElement } from '@/components/ui/Reveal'
import SectionIntro from '@/components/ui/SectionIntro'
import styles from './HowWeWork.module.css'

// Each step: name, one line on what happens, and (optionally) a youGet line.
// Default = the homepage copy. Service pages pass their own via `steps`.
// False on the server and first client render so markup hydrates identically
function useReducedMotionAfterMount() {
    const reduced = useReducedMotion()
    const [mounted, setMounted] = useState(false)
    useEffect(() => setMounted(true), [])
    return mounted && !!reduced
}

export const defaultSteps = [
    {
        title: 'Talk',
        description: 'Tell us what your team does by hand today.',
    },
    {
        title: 'Plan',
        description: 'You get a written plan and a price before any work starts.',
    },
    {
        title: 'Build',
        description: 'We build it and test it on your real data.',
    },
    {
        title: 'Support',
        description: 'We watch it, fix what breaks, and improve it.',
    },
]

// Where on screen the line's tip sits while drawing (65% down the viewport)
const DRAW_LINE = 'start 65%'
const DRAW_END = 'end 65%'

/*
 * Props (all optional, defaults = homepage):
 * - eyebrow, title, description: section intro
 * - steps: [{ title, description, youGet? }] (any length; youGet line is
 *   omitted when missing)
 * - youGetLabel: label before each youGet line (default 'You get:')
 * - className: appended to the outer <section> (use a tone class,
 *   e.g. 'section-shell--stone')
 */
export default function HowWeWork({
    eyebrow = 'Process',
    title = 'How it works',
    description = 'The same four steps for every project.',
    steps = defaultSteps,
    youGetLabel = 'You get:',
    className = '',
} = {}) {
    // Eyebrows no longer carry section numbers ("05 / How we work" -> "How we work")
    const eyebrowText = eyebrow?.replace(/^\s*\d+\s*\/\s*/, '')

    const listRef = useRef(null)
    const trackRef = useRef(null)
    const nodeRefs = useRef([])
    const reduceMotion = useReducedMotionAfterMount()

    // Track geometry (first node center to last node center) and each node's
    // position along it as a 0-1 threshold
    const [track, setTrack] = useState({ top: 7, height: 0 })
    const [thresholds, setThresholds] = useState([])
    const [reachedCount, setReachedCount] = useState(0)

    // Progress is 0 when the track's top hits the draw line and 1 when its bottom does,
    // so the line tip always sits at the same spot on screen
    const { scrollYProgress } = useScroll({
        target: trackRef,
        offset: [DRAW_LINE, DRAW_END],
    })

    const measure = useCallback(() => {
        const list = listRef.current
        const nodes = nodeRefs.current.filter(Boolean)
        if (!list || nodes.length === 0) {
            return
        }
        const listTop = list.getBoundingClientRect().top
        const centers = nodes.map((node) => {
            const rect = node.getBoundingClientRect()
            return rect.top - listTop + rect.height / 2
        })
        const first = centers[0]
        const length = Math.max(centers[centers.length - 1] - first, 1)
        setTrack({ top: first, height: length })
        setThresholds(centers.map((center) => (center - first) / length))
    }, [])

    // Measure on mount and whenever the list reflows
    useEffect(() => {
        measure()
        const list = listRef.current
        if (!list || typeof ResizeObserver === 'undefined') {
            return undefined
        }
        const observer = new ResizeObserver(() => measure())
        observer.observe(list)
        return () => observer.disconnect()
    }, [measure])

    const updateReached = useCallback(
        (progress) => {
            // Small epsilon so the last node fills when the line finishes
            setReachedCount(thresholds.filter((t) => progress >= t - 0.001).length)
        },
        [thresholds]
    )

    // Sync once thresholds are known (e.g. page loaded mid-section)
    useEffect(() => {
        updateReached(scrollYProgress.get())
    }, [updateReached, scrollYProgress])

    useMotionValueEvent(scrollYProgress, 'change', updateReached)

    return (
        <section className={`section-shell relative ${className}`.trim()}>
            <div className="relative container">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                    {/* Heading: sticky on desktop while the steps scroll past */}
                    <div className="lg:col-span-5">
                        <div className={styles.stickyIntro}>
                            <AnimatedElement>
                                <SectionIntro
                                    eyebrow={eyebrowText}
                                    title={title}
                                    description={description}
                                    align="left"
                                />
                            </AnimatedElement>
                        </div>
                    </div>

                    {/* Process spine: the line draws down with scroll, nodes fill as it reaches them */}
                    <div className="lg:col-span-7">
                        <div ref={listRef} className={styles.spine}>
                            <div
                                ref={trackRef}
                                className={styles.track}
                                style={{ top: track.top, height: track.height || undefined }}
                                aria-hidden="true"
                            >
                                <motion.div
                                    className={styles.trackFill}
                                    style={{ scaleY: reduceMotion ? 1 : scrollYProgress }}
                                />
                            </div>

                            <ol className={styles.steps}>
                            {steps.map((step, index) => {
                                const reached = reduceMotion || index < reachedCount
                                return (
                                    <li
                                        key={`${index}-${step.title}`}
                                        className={styles.step}
                                        data-reached={reached ? 'true' : 'false'}
                                    >
                                        <span
                                            ref={(node) => {
                                                nodeRefs.current[index] = node
                                            }}
                                            className={styles.node}
                                            aria-hidden="true"
                                        />
                                        <span className={styles.stepLabel}>
                                            Step {String(index + 1).padStart(2, '0')}
                                        </span>
                                        <h3 className={styles.stepTitle}>{step.title}</h3>
                                        <p className={styles.stepDescription}>{step.description}</p>
                                        {step.youGet ? (
                                            <p className={styles.youGet}>
                                                <span className={styles.youGetLabel}>{youGetLabel}</span>{' '}
                                                {step.youGet}
                                            </p>
                                        ) : null}
                                    </li>
                                )
                            })}
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
